import app from 'flarum/forum/app';
import Page from 'flarum/common/components/Page';
import Link from 'flarum/common/components/Link';
import humanTime from 'flarum/common/helpers/humanTime';
import icon from 'flarum/common/helpers/icon';

const RESULT_LIMIT = 20;

export default class MobileSearchPage extends Page {
  oninit(vnode) {
    super.oninit(vnode);
    this.query = String(m.route.param('q') || '').trim();
    this.results = [];
    this.loading = false;
    this.hasMore = false;
    this.hasSearched = false;
    this.failed = false;
    this.searchTimer = null;
    this.requestId = 0;
    app.setTitle(app.translator.trans('space-pocket-posts.forum.bottom_nav.search'));
  }

  oncreate(vnode) {
    super.oncreate(vnode);

    window.requestAnimationFrame(() => {
      const input = vnode.dom.querySelector('.SpacePocketPosts-searchInput');

      if (input) input.focus();
    });

    if (this.query) this.loadResults(true);
  }

  onremove(vnode) {
    if (this.searchTimer) window.clearTimeout(this.searchTimer);
    this.requestId++;
    super.onremove(vnode);
  }

  search(event) {
    this.query = event.currentTarget.value;
    this.requestId++;

    if (this.searchTimer) window.clearTimeout(this.searchTimer);

    if (!this.query.trim()) {
      this.results = [];
      this.loading = false;
      this.hasMore = false;
      this.hasSearched = false;
      this.failed = false;
      return;
    }

    this.searchTimer = window.setTimeout(() => this.loadResults(true), 260);
  }

  handleKeydown(event) {
    if (event.key !== 'Enter') return;

    event.preventDefault();
    if (this.searchTimer) window.clearTimeout(this.searchTimer);
    this.loadResults(true);
  }

  clear() {
    if (this.searchTimer) window.clearTimeout(this.searchTimer);
    this.requestId++;
    this.query = '';
    this.results = [];
    this.loading = false;
    this.hasMore = false;
    this.hasSearched = false;
    this.failed = false;
    m.redraw();

    window.requestAnimationFrame(() => document.querySelector('.SpacePocketPosts-searchInput')?.focus());
  }

  async loadResults(reset) {
    const query = this.query.trim();

    if (!query || (!reset && this.loading)) return;

    const requestId = ++this.requestId;
    const offset = reset ? 0 : this.results.length;
    this.loading = true;
    this.failed = false;
    this.hasSearched = true;

    if (reset) {
      this.results = [];
      this.hasMore = false;
    }

    m.redraw();

    try {
      const response = await app.request({
        method: 'GET',
        url:
          app.forum.attribute('apiUrl') +
          '/space-pocket/search?filter[q]=' +
          encodeURIComponent(query) +
          '&page[offset]=' +
          offset +
          '&page[limit]=' +
          (RESULT_LIMIT + 1),
      });

      if (requestId !== this.requestId) return;

      const data = Array.isArray(response.data) ? response.data : [];
      const mapped = this.mapResults(data.slice(0, RESULT_LIMIT), response.included || []);

      this.results = reset ? mapped : this.uniqueResults([...this.results, ...mapped]);
      this.hasMore = data.length > RESULT_LIMIT;
    } catch (error) {
      if (requestId === this.requestId) this.failed = true;
    } finally {
      if (requestId === this.requestId) {
        this.loading = false;
        m.redraw();
      }
    }
  }

  mapResults(resources, included) {
    const includedById = new Map(
      included.map((resource) => [resource.type + ':' + resource.id, resource])
    );

    return resources.map((resource) => {
      const attributes = resource.attributes || {};
      const userReference = resource.relationships?.user?.data;
      const user = userReference
        ? includedById.get(userReference.type + ':' + userReference.id)?.attributes || {}
        : {};

      return {
        id: String(resource.id),
        title: attributes.title || '',
        href: app.route('discussion', { id: attributes.slug || resource.id }),
        excerpt: attributes.spacePocketSearchExcerpt || '',
        topics: Array.isArray(attributes.spacePocketTopics) ? attributes.spacePocketTopics : [],
        replies: Math.max(0, Number(attributes.commentCount || 1) - 1),
        lastPostedAt: attributes.lastPostedAt ? new Date(attributes.lastPostedAt) : null,
        user: user.displayName || user.username || '',
        avatarUrl: user.avatarUrl || '',
      };
    });
  }

  uniqueResults(results) {
    const seen = new Set();

    return results.filter((result) => {
      if (seen.has(result.id)) return false;
      seen.add(result.id);
      return true;
    });
  }

  resultView(result) {
    return (
      <li className="SpacePocketPosts-searchResult" key={result.id}>
        <Link href={result.href} className="SpacePocketPosts-searchResultLink">
          <div className="SpacePocketPosts-searchResultHeader">
            <h2>{result.title}</h2>
            {result.lastPostedAt && <time>{humanTime(result.lastPostedAt)}</time>}
          </div>

          {result.excerpt && <p>{result.excerpt}</p>}

          <div className="SpacePocketPosts-searchResultMeta">
            {result.avatarUrl && <img src={result.avatarUrl} alt="" className="Avatar" loading="lazy" />}
            {result.user && <span>{result.user}</span>}
            <span>{icon('far fa-comment')} {result.replies}</span>
            {result.topics.map((topic) => (
              <span className="SpacePocketPosts-searchResultTopic" key={result.id + '-' + topic}>#{topic}</span>
            ))}
          </div>
        </Link>
      </li>
    );
  }

  view() {
    return (
      <div className="SpacePocketPosts-searchPage">
        <div className="container">
          <h1>{app.translator.trans('space-pocket-posts.forum.bottom_nav.search')}</h1>

          <div
            className="SpacePocketPosts-searchField"
            role="search"
            aria-label={app.translator.trans('space-pocket-posts.forum.bottom_nav.search')}
          >
            {icon('fas fa-search')}
            <input
              type="text"
              inputmode="search"
              enterkeyhint="search"
              className="FormControl SpacePocketPosts-searchInput"
              value={this.query}
              maxlength="80"
              placeholder={app.translator.trans('space-pocket-posts.forum.bottom_nav.search')}
              aria-label={app.translator.trans('space-pocket-posts.forum.bottom_nav.search')}
              oninput={(event) => this.search(event)}
              onkeydown={(event) => this.handleKeydown(event)}
            />
            {this.query && (
              <button
                type="button"
                className="Button Button--icon Button--link SpacePocketPosts-searchClear"
                aria-label={app.translator.trans('space-pocket-posts.forum.search.clear')}
                onclick={() => this.clear()}
              >
                {icon('fas fa-times-circle')}
              </button>
            )}
          </div>

          <div className="SpacePocketPosts-searchResults" aria-live="polite">
            {this.failed && (
              <p className="SpacePocketPosts-searchStatus">
                {app.translator.trans('space-pocket-posts.forum.search.failed')}
              </p>
            )}
            {!this.loading && !this.failed && this.hasSearched && !this.results.length && (
              <p className="SpacePocketPosts-searchStatus">
                {app.translator.trans('space-pocket-posts.forum.search.empty')}
              </p>
            )}
            {this.results.length > 0 && (
              <ul className="SpacePocketPosts-searchResultList">
                {this.results.map((result) => this.resultView(result))}
              </ul>
            )}
            {this.loading && (
              <p className="SpacePocketPosts-searchStatus">
                {icon('fas fa-spinner', { className: 'fa-spin' })}
              </p>
            )}
            {!this.loading && this.hasMore && (
              <button type="button" className="Button SpacePocketPosts-searchMore" onclick={() => this.loadResults(false)}>
                {app.translator.trans('space-pocket-posts.forum.search.load_more')}
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }
}
